import { Dispatch, Fragment, SetStateAction, useMemo } from "react";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";

import { AvailableModuleData } from "../../ReferencesHooks/useAvailableModules";
import { userRecordActivityAtom } from "../../../../../atoms/GlobalAtoms";
import { getMetadataFromRecord } from "../../../../../utils/RecordUtils";
import { Field } from "../../../../../types/TenantInterfaces";
import SelectableRow from "../../../../Inputs/SelectableRow";

interface ReferencesListProps {
  records: any[];
  modulesMap: Map<string, AvailableModuleData>;
  isRecordSelected: (record: any) => boolean;
  handleSelectRecord: (record: any) => void;
  handleOpen: () => void;
  handleClose: () => void;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  isSubitemReference: boolean;
  subitemField: Field | undefined;
  currentKey: string;
  selectedSubitemParent: any;
  setSelectedSubitemParent: Dispatch<SetStateAction<any>>;
  favorites: string[];
  showDisplayButton?: boolean;
}

const ReferencesList = ({
  records,
  modulesMap,
  isRecordSelected,
  handleSelectRecord,
  handleOpen,
  handleClose,
  setKeepOpen,
  isSubitemReference,
  subitemField,
  currentKey,
  selectedSubitemParent,
  setSelectedSubitemParent,
  favorites,
  showDisplayButton,
}: ReferencesListProps) => {
  const userRecordActivity = useAtomValue(userRecordActivityAtom);

  const modObj = useMemo(() => {
    return modulesMap.get(currentKey);
  }, [currentKey, modulesMap]);

  const metadataFields = useMemo(() => {
    const foundFields = [];

    if (modObj) {
      const { MetadataField, MetadataFieldType } = modObj;
      MetadataField && foundFields.push({ MetadataField, MetadataFieldType });
    }

    const moduleAPIName = modObj?.Module?.APIName ?? "";
    if (
      !userRecordActivity?.[moduleAPIName] ||
      !userRecordActivity?.[moduleAPIName]?.Fields?.length
    )
      return foundFields;

    return userRecordActivity?.[moduleAPIName]?.Fields.flatMap((fAPIName) => {
      const field = findWhere(modObj?.Module?.Fields ?? [], {
        APIName: fAPIName,
      });

      if (!field) return [];

      return [
        {
          MetadataField: field.APIName,
          MetadataFieldType: field.FieldType,
        },
      ];
    });
  }, [modObj]);

  if (!records || !records?.length) return null;

  return records?.map((record, i) => {
    if (!modObj) return null;

    const { ImageField, ImagePresentation } = modObj;

    const validData = metadataFields
      .map((metadataField) =>
        getMetadataFromRecord(
          record,
          metadataField.MetadataField,
          metadataField.MetadataFieldType
        )
      )
      .filter((data) => data);

    const description = validData.length ? (
      <div>
        {validData.map((data, index) => (
          <Fragment key={index}>
            <span>{data}</span>
            {index < validData.length - 1 && <br />}
          </Fragment>
        ))}
      </div>
    ) : null;

    const isSelected = isRecordSelected(record);

    const recordName =
      modObj?.Module?.Templates?.RecordListView?.RecordNameField;
    const emptyRecordName =
      modObj?.Module?.Templates?.RecordListView?.RecordNameEmpty;

    const displayName =
      selectedSubitemParent && subitemField?.SubitemRecordNameField
        ? record?.[subitemField?.SubitemRecordNameField]
        : record?.[recordName || "_RecordName"];

    const markedAsFavorite = (favorites || [])?.includes(record?.id);

    if (isSubitemReference && !selectedSubitemParent) {
      return (
        <Fragment key={i}>
          <SelectableRow
            item={record}
            handleSelectItem={setSelectedSubitemParent}
            onOpen={handleOpen}
            onClose={handleClose}
            displayName={displayName || emptyRecordName || "(No Name)"}
            description={description}
            setKeepOpen={setKeepOpen}
            imageField={ImageField}
            imageFieldPresentation={ImagePresentation}
            selectable={false}
            showArrowButton={true}
            showDisplayButton={showDisplayButton}
            isFavorite={markedAsFavorite}
            subitemField={undefined}
            subitemRecord={undefined}
            // TODO: Check this.
            isItemSelected={isSelected}
            index={i}
          />
          {i !== records.length - 1 && <hr className="my-1"></hr>}
        </Fragment>
      );
    }

    return (
      <Fragment key={i}>
        <SelectableRow
          item={record}
          handleSelectItem={handleSelectRecord}
          isItemSelected={isSelected}
          displayName={displayName || "(No Name)"}
          onOpen={handleOpen}
          onClose={handleClose}
          description={description}
          setKeepOpen={setKeepOpen}
          imageField={ImageField}
          imageFieldPresentation={ImagePresentation}
          subitemField={subitemField}
          subitemRecord={selectedSubitemParent}
          isFavorite={markedAsFavorite}
          showDisplayButton={showDisplayButton}
          index={i}
        />
        {i !== records.length - 1 && <hr className="my-1"></hr>}
      </Fragment>
    );
  });
};

export default ReferencesList;
